import { useRef, useState, useCallback, useEffect, MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent, ReactRenderer, ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Image, { type ImageOptions } from '@tiptap/extension-image';
import Mention from '@tiptap/extension-mention';
import { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Code,
  Heading2,
  Undo,
  Redo,
  Paperclip,
  X,
  FileText,
  Loader2,
  Smile,
  Film,
  Type,
  ImageOff,
} from 'lucide-react';
import { cn, formatBytes } from '@/lib/utils';
import { extractFilesFromDataTransfer } from '../../lib/fileSystemUtils';
import { MentionList, MentionListRef, MentionUser, EVERYONE_MENTION, EVERYONE_MENTION_ID } from './MentionList';
import { GifPicker } from './GifPicker';

export interface StagedFile {
  id: string;
  file: File;
  preview?: string;
  uploading?: boolean;
  error?: string;
  /** Already uploaded (an embed moved back out of the content). Linked on post, not re-uploaded. */
  attachmentId?: string;
}

/** An inline image the writer moved out of the content and into the attachments. */
export interface DemotedEmbed {
  attachmentId: string;
  url: string;
  fileName: string;
}

interface RichTextEditorProps {
  content?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
  editorClassName?: string;
  minHeight?: string;
  /**
   * Scroll the writing area past this height instead of growing. A composer
   * that sits below a scrolling pane (the discussion takeover) has nowhere to
   * grow to: one tall embed pushed it straight through the card.
   */
  maxHeight?: string;
  showToolbar?: boolean;
  /**
   * Phone-first: start with NO formatting toolbar and an "Aa" toggle that
   * reveals it. Most replies are a sentence — the full three rows of
   * controls cost more screen than the message being answered (Jesse,
   * 2026-08-27). Desktop is unaffected: the toolbar shows as always.
   */
  compactToolbar?: boolean;
  // Attachment support
  enableAttachments?: boolean;
  stagedFiles?: StagedFile[];
  onFilesAdded?: (files: File[]) => void;
  onFileRemoved?: (fileId: string) => void;
  disabled?: boolean;
  // Mentions support
  enableMentions?: boolean;
  mentionableUsers?: MentionUser[];
  mentionUsersLoading?: boolean;
  onMention?: (userId: string) => void;
  /** Offer an "@Everyone" entry that notifies all project members. Only enable
   * on surfaces whose submit path knows how to expand it (discussions, task comments). */
  enableEveryoneMention?: boolean;
  // GIF picker support
  enableGifPicker?: boolean;
  /**
   * Inline image embeds. When set, a pasted or dropped image is uploaded at
   * once and placed in the content at the cursor (paste-embed is the default);
   * other files still stage as attachments. Resolves to the attachment id and
   * a URL the editor can show right away.
   */
  onEmbedImage?: (file: File) => Promise<{ attachmentId: string; url: string }>;
  /** "Move to attachments" on an embed: the parent keeps it staged as a plain attachment. */
  onDemoteEmbed?: (embed: DemotedEmbed) => void;
}

const ToolbarButton = ({
  onClick,
  isActive,
  disabled,
  children,
  title,
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    tabIndex={-1}
    className={cn(
      'p-1.5 rounded transition-colors',
      isActive
        ? 'bg-secondary text-white'
        : 'text-grey-600 hover:bg-grey-100',
      disabled && 'opacity-50 cursor-not-allowed'
    )}
  >
    {children}
  </button>
);

const ALL_EMOJIS = [
  // Frequently used
  '👍', '👎', '❤️', '🎉', '😄', '😮', '👀', '🙏', '✅', '❌',
  // Emotions
  '😀', '😃', '😊', '🥰', '😍', '🤔', '😅', '😂', '🥳', '🤩',
  '😢', '😭', '😤', '😠', '🤯', '😱', '🙄', '😴', '🤮', '🤢',
  // Gestures
  '👏', '🙌', '💪', '🤝', '👋', '✌️', '🤞', '🫡', '🫶', '👊',
  // Objects & Symbols
  '🔥', '💯', '⭐', '💡', '🚀', '💎', '🏆', '🎯', '📌', '💬',
  '✨', '💥', '🌟', '⚡', '🔔', '📢', '🎨', '🛠️', '📝', '📎',
];

function EmojiPicker({ anchorRef, onSelect }: { anchorRef: React.RefObject<HTMLDivElement>; onSelect: (emoji: string) => void }) {
  const pickerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ bottom: number; left: number } | null>(null);

  // Position above anchor, bottom edge attached to the top of the icon button
  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setCoords({
      bottom: window.innerHeight - rect.top + 4,
      left: rect.left,
    });
  }, [anchorRef]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        pickerRef.current && !pickerRef.current.contains(event.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(event.target as Node)
      ) {
        onSelect('__close__');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSelect, anchorRef]);

  if (!coords) return null;

  return createPortal(
    <div
      ref={pickerRef}
      style={{ position: 'fixed', bottom: coords.bottom, left: coords.left, zIndex: 9999 }}
      className="bg-white rounded-2xl shadow-xl border border-grey-200 p-3 min-w-[260px]"
    >
      <div className="grid grid-cols-8 gap-1">
        {ALL_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            tabIndex={-1}
            onClick={() => onSelect(emoji)}
            className="w-8 h-8 flex items-center justify-center text-lg hover:bg-grey-100 rounded-lg transition-colors"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}

// Create suggestion config for mentions
const createMentionSuggestion = (
  usersRef: MutableRefObject<MentionUser[]>,
  isLoadingRef: MutableRefObject<boolean>,
  onMention?: (userId: string) => void,
  includeEveryone = false
) => ({
  items: ({ query }: { query: string }) => {
    const matches = usersRef.current
      .filter((user) =>
        user.name.toLowerCase().includes(query.toLowerCase()) ||
        user.email.toLowerCase().includes(query.toLowerCase())
      )
      .slice(0, 5);
    if (includeEveryone && 'everyone'.startsWith(query.toLowerCase())) {
      return [EVERYONE_MENTION, ...matches];
    }
    return matches;
  },
  render: () => {
    let component: ReactRenderer<MentionListRef> | null = null;
    let popup: TippyInstance[] | null = null;

    return {
      onStart: (props: SuggestionProps<MentionUser>) => {
        component = new ReactRenderer(MentionList, {
          props: {
            items: props.items,
            isLoading: isLoadingRef.current,
            command: (item: { id: string; label: string }) => {
              props.command(item);
              props.editor.commands.insertContent(' ');
              onMention?.(item.id);
            },
          },
          editor: props.editor,
        });

        if (!props.clientRect) return;

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
        });
      },
      onUpdate: (props: SuggestionProps<MentionUser>) => {
        component?.updateProps({
          items: props.items,
          isLoading: isLoadingRef.current,
          command: (item: { id: string; label: string }) => {
            props.command(item);
            props.editor.commands.insertContent(' ');
            onMention?.(item.id);
          },
        });

        if (!props.clientRect) return;

        popup?.[0]?.setProps({
          getReferenceClientRect: props.clientRect as () => DOMRect,
        });
      },
      onKeyDown: (props: SuggestionKeyDownProps) => {
        if (props.event.key === 'Escape') {
          popup?.[0]?.hide();
          return true;
        }
        return component?.ref?.onKeyDown(props) ?? false;
      },
      onExit: () => {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  },
});

interface EmbeddedImageOptions extends ImageOptions {
  onDemote?: (embed: DemotedEmbed) => void;
}

/**
 * The image node with its attachment identity. `data-attachment-id` is what
 * survives in stored content; `src` is a presigned URL that will expire and is
 * re-resolved on every render (see lib/embeds.ts). `uploading` / `tempId` live
 * only in the editor while an upload is in flight and never reach the HTML.
 */
/**
 * One `src`, one load lifecycle. Keyed by src from the parent so a new URL
 * (preview → uploaded) starts from "loading" again: the reader sees a spinner,
 * then the picture, and never the browser's broken-image glyph.
 */
function EmbedPicture({ src, alt, dimmed, label, onRemove }: {
  src: string;
  alt: string;
  dimmed: boolean;
  label: string | null;
  onRemove: () => void;
}) {
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  if (state === 'failed') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-error/40 bg-error/5 px-3 py-2 text-xs text-error">
        <ImageOff className="h-4 w-4 shrink-0" />
        <span>Couldn&apos;t load {alt || 'this image'}.</span>
        <button type="button" onClick={onRemove} className="ml-1 font-medium underline hover:no-underline">
          Remove
        </button>
      </div>
    );
  }
  const busy = dimmed || state === 'loading';
  return (
    <>
      <img
        src={src}
        alt={alt}
        onLoad={() => setState('ready')}
        onError={() => setState('failed')}
        className={cn('h-auto max-h-[24rem] w-auto max-w-full rounded-lg transition-opacity', busy && 'opacity-40')}
        draggable={false}
      />
      {busy && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg text-xs font-medium text-grey-600">
          <Loader2 className="h-5 w-5 animate-spin text-accent" />
          {label ?? (state === 'loading' ? 'Loading image…' : null)}
        </div>
      )}
    </>
  );
}

function EmbeddedImageView({ node, editor, deleteNode, extension, selected }: NodeViewProps) {
  const { src, alt, attachmentId, uploading } = node.attrs as {
    src: string;
    alt?: string | null;
    attachmentId?: string | null;
    uploading?: boolean;
  };
  const onDemote = (extension.options as EmbeddedImageOptions).onDemote;
  const canAct = editor.isEditable && !!attachmentId && !uploading;

  return (
    <NodeViewWrapper
      as="div"
      className={cn('group relative my-2 inline-block max-w-full min-h-[3rem] min-w-[8rem]', selected && 'rounded-lg ring-2 ring-accent')}
      data-drag-handle
    >
      {src ? (
        <EmbedPicture
          key={src}
          src={src}
          alt={alt ?? ''}
          dimmed={!!uploading}
          label={uploading ? 'Uploading image…' : null}
          onRemove={() => deleteNode()}
        />
      ) : (
        <div className="flex items-center gap-2 rounded-lg bg-grey-50 px-3 py-2 text-xs text-grey-500">
          <Loader2 className="h-4 w-4 animate-spin text-accent" />
          Uploading image…
        </div>
      )}
      {canAct && (
        <div
          className={cn('absolute right-1 top-1 gap-1', selected ? 'flex' : 'hidden group-hover:flex')}
          contentEditable={false}
        >
          <button
            type="button"
            onClick={() => {
              onDemote?.({ attachmentId: attachmentId!, url: src, fileName: alt || 'image' });
              deleteNode();
            }}
            className="rounded-md border border-grey-200 bg-white/95 px-2 py-1 text-[11px] font-medium text-secondary shadow-sm hover:bg-grey-50"
          >
            Move to attachments
          </button>
          <button
            type="button"
            onClick={() => deleteNode()}
            aria-label="Remove image"
            className="rounded-md border border-grey-200 bg-white/95 p-1 text-grey-500 shadow-sm hover:bg-grey-50 hover:text-error"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </NodeViewWrapper>
  );
}

const EmbeddedImage = Image.extend<EmbeddedImageOptions>({
  addOptions() {
    return { ...this.parent?.(), onDemote: undefined };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      attachmentId: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-attachment-id'),
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs.attachmentId ? { 'data-attachment-id': attrs.attachmentId } : {},
      },
      uploading: { default: false, rendered: false },
      tempId: { default: null, rendered: false },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(EmbeddedImageView);
  },
});

interface EmbedHit {
  pos: number;
  attrs: Record<string, unknown>;
  size: number;
}

/**
 * Insert an embed and put the cursor after it. insertContent leaves a block
 * image node-selected, and a node selection is replaced by the next paste or
 * keystroke, so two quick pastes would leave one image. A paragraph is added
 * when the image lands at the end of the document so there is somewhere to go.
 */
function insertEmbedNode(ed: Editor, attrs: Record<string, unknown>) {
  ed.chain()
    .focus()
    .insertContent({ type: 'image', attrs })
    .command(({ tr, dispatch }) => {
      if (!dispatch) return true;
      const sel = tr.selection as unknown as { node?: { type: { name: string } }; to: number };
      if (sel.node?.type.name !== 'image') return true;
      const pos = sel.to;
      if (!tr.doc.resolve(pos).nodeAfter) {
        tr.insert(pos, tr.doc.type.schema.nodes.paragraph.create());
      }
      const Sel = tr.selection.constructor as unknown as { near: (p: unknown, bias: number) => typeof tr.selection };
      tr.setSelection(Sel.near(tr.doc.resolve(pos), 1));
      return true;
    })
    .run();
}

/**
 * The GIF a page copy is really about. "Copy image" on a GIF (Giphy, Slack, a
 * blog) puts a single-frame PNG in `files` and `<img src="….gif">` in the HTML.
 * The file is what we would upload; the URL is the animation. Returns that URL
 * when the pasted HTML is one remote GIF, else null.
 */
function pastedGifUrl(html: string): string | null {
  if (!html || !/<img\b/i.test(html)) return null;
  try {
    const imgs = new DOMParser().parseFromString(html, 'text/html').querySelectorAll('img[src]');
    if (imgs.length !== 1) return null;
    const url = new URL(imgs[0].getAttribute('src') ?? '');
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.pathname.toLowerCase().endsWith('.gif') ? url.toString() : null;
  } catch {
    return null;
  }
}

/** Console breadcrumb for the one path we cannot show in the UI. */
function embedWarn(message: string, detail?: unknown) {
  // eslint-disable-next-line no-console
  console.warn(`[embed] ${message}`, detail ?? '');
}

export function RichTextEditor({
  content = '',
  onChange,
  placeholder = 'Write something...',
  className,
  editorClassName,
  minHeight = '120px',
  maxHeight,
  showToolbar = true,
  compactToolbar = false,
  enableAttachments = false,
  stagedFiles = [],
  onFilesAdded,
  onFileRemoved,
  disabled = false,
  enableMentions = false,
  mentionableUsers = [],
  mentionUsersLoading = false,
  onMention,
  enableEveryoneMention = false,
  enableGifPicker = false,
  onEmbedImage,
  onDemoteEmbed,
}: RichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiAnchorRef = useRef<HTMLDivElement>(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  // Compact mode hides the toolbar until asked; desktop never asks.
  const [formatOpen, setFormatOpen] = useState(false);
  const gifAnchorRef = useRef<HTMLDivElement>(null);

  // The editor's paste/drop handlers are bound once; these refs keep them on
  // the current callbacks and editor instance.
  const editorRef = useRef<Editor | null>(null);
  const onEmbedImageRef = useRef(onEmbedImage);
  const onDemoteEmbedRef = useRef(onDemoteEmbed);
  const onFilesAddedRef = useRef(onFilesAdded);
  useEffect(() => {
    onEmbedImageRef.current = onEmbedImage;
    onDemoteEmbedRef.current = onDemoteEmbed;
    onFilesAddedRef.current = onFilesAdded;
  }, [onEmbedImage, onDemoteEmbed, onFilesAdded]);

  /**
   * Update (patch) or remove (null) the in-flight embed. Matched by tempId,
   * or by its preview src as a fallback: tempId is not serialized, so an
   * editor that was re-created from HTML mid-upload only has the src.
   * Returns whether a node was found.
   */
  const patchEmbed = useCallback((tempId: string, previewSrc: string, patch: Record<string, unknown> | null): boolean => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return false;
    const { state } = ed;
    const hits: EmbedHit[] = [];
    state.doc.descendants((node, pos) => {
      if (hits.length) return false;
      if (node.type.name === 'image' && (node.attrs.tempId === tempId || node.attrs.src === previewSrc)) {
        hits.push({ pos, attrs: node.attrs, size: node.nodeSize });
        return false;
      }
      return true;
    });
    const hit = hits[0];
    if (!hit) return false;
    const tr = patch
      ? state.tr.setNodeMarkup(hit.pos, undefined, { ...hit.attrs, ...patch })
      : state.tr.delete(hit.pos, hit.pos + hit.size);
    ed.view.dispatch(tr);
    return true;
  }, []);

  /**
   * Paste-embed: the image goes into the content at once (local preview, then
   * the uploaded URL). If the upload fails it falls back to a plain attachment
   * so nothing the writer pasted is lost.
   */
  const embedImages = useCallback((files: File[]): boolean => {
    const ed = editorRef.current;
    const upload = onEmbedImageRef.current;
    if (!ed || !upload) return false;
    for (const file of files) {
      const tempId = `embed-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const preview = URL.createObjectURL(file);
      insertEmbedNode(ed, { src: preview, alt: file.name, tempId, uploading: true });
      upload(file)
        .then(({ attachmentId, url }) => {
          // The preview is only released once nothing points at it; a node we
          // cannot find keeps its preview rather than turning into a broken image.
          if (patchEmbed(tempId, preview, { src: url, attachmentId, uploading: false, tempId: null })) {
            URL.revokeObjectURL(preview);
          } else {
            embedWarn('uploaded image not found in the editor; preview left in place', { file: file.name, attachmentId });
          }
        })
        .catch((err) => {
          embedWarn('image upload failed; staging it as an attachment instead', err);
          if (patchEmbed(tempId, preview, null)) URL.revokeObjectURL(preview);
          onFilesAddedRef.current?.([file]);
        });
    }
    return true;
  }, [patchEmbed]);

  /** Put a demoted (already uploaded) embed back into the content. */
  const reinsertEmbed = useCallback((staged: StagedFile) => {
    const ed = editorRef.current;
    if (!ed || !staged.attachmentId || !staged.preview) return;
    insertEmbedNode(ed, { src: staged.preview, alt: staged.file.name, attachmentId: staged.attachmentId });
  }, []);

  /** Images embed when the surface can upload them; everything else stages. */
  const routeFiles = useCallback((files: File[]): boolean => {
    const canEmbed = !!onEmbedImageRef.current;
    const images = canEmbed ? files.filter((f) => f.type.startsWith('image/')) : [];
    const rest = canEmbed ? files.filter((f) => !f.type.startsWith('image/')) : files;
    let handled = false;
    if (images.length) handled = embedImages(images) || handled;
    if (rest.length && enableAttachments && onFilesAddedRef.current) {
      onFilesAddedRef.current(rest);
      handled = true;
    }
    return handled;
  }, [embedImages, enableAttachments]);

  // Remove webkitdirectory attribute that some browsers/extensions add to file inputs,
  // which causes the file picker to only allow folder selection instead of files.
  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute('webkitdirectory');
    }
  }, []);

  const isLoadingRef = useRef(mentionUsersLoading);
  const usersRef = useRef(mentionableUsers);
  useEffect(() => {
    isLoadingRef.current = mentionUsersLoading;
  }, [mentionUsersLoading]);
  useEffect(() => {
    usersRef.current = mentionableUsers;
  }, [mentionableUsers]);

  // Remove webkitdirectory attribute to prevent folder-only mode in file picker
  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute('webkitdirectory');
    }
  }, []);

  const handleFiles = useCallback((files: FileList | File[]) => {
    if (onFilesAdded) {
      onFilesAdded(Array.from(files));
    }
  }, [onFilesAdded]);

  // Build extensions array
  const extensions = [
    StarterKit.configure({
      heading: {
        levels: [2, 3],
      },
      // Disable StarterKit's built-in Link extension — we configure our own below
      // with autolink: false to prevent cursor trapping after paste (Issue #29)
      link: false,
    }),
    Placeholder.configure({
      placeholder,
      emptyEditorClass: 'before:content-[attr(data-placeholder)] before:text-grey-400 before:float-left before:pointer-events-none before:h-0',
    }),
    Link.configure({
      openOnClick: false,
      // autolink: false makes the link mark non-inclusive (see Tiptap source: inclusive() { return this.options.autolink })
      // This means the cursor naturally sits outside the link mark after paste — no manual escape needed.
      // linkOnPaste still applies link marks when pasting URLs/emails (default: true).
      autolink: false,
      HTMLAttributes: {
        class: 'text-accent underline',
      },
    }),
    Underline,
    EmbeddedImage.configure({
      HTMLAttributes: {
        class: 'max-w-full h-auto rounded-lg my-2',
      },
      onDemote: (embed: DemotedEmbed) => onDemoteEmbedRef.current?.(embed),
    }),
  ];

  // Add mention extension if enabled
  if (enableMentions) {
    extensions.push(
      Mention.configure({
        HTMLAttributes: {
          class: 'mention bg-primary/10 text-primary px-1 py-0.5 rounded font-medium',
        },
        suggestion: createMentionSuggestion(usersRef, isLoadingRef, onMention, enableEveryoneMention),
      })
    );
  }

  const editor = useEditor({
    extensions,
    content,
    editable: !disabled,
    onUpdate: ({ editor, transaction }) => {
      // After paste: if the cursor landed inside a link mark, move it to just
      // after the end of that mark so subsequent typing is plain text.
      // Simply clearing storedMarks isn't enough — the cursor itself is
      // positioned inside the <a> DOM node and types extend it from within.
      if (transaction.getMeta('paste')) {
        const { state } = editor;
        const { $from } = state.selection;
        const linkMark = state.schema.marks.link;
        if (linkMark && $from.marks().some((m) => m.type === linkMark)) {
          // Find the end of the link mark range at the current position
          const end = $from.end();
          // Walk forward from current pos to find where the link mark ends
          let linkEnd = $from.pos;
          state.doc.nodesBetween($from.pos, end, (node, pos) => {
            if (node.isText && node.marks.some((m) => m.type === linkMark)) {
              linkEnd = pos + node.nodeSize;
            }
          });
          // Move cursor to just after the link, with no stored marks
          const tr = state.tr
            .setSelection(state.selection.constructor.create(state.doc, linkEnd))
            .setStoredMarks([]);
          editor.view.dispatch(tr);
        }
      }
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none focus:outline-none break-words',
          'prose-headings:font-serif prose-headings:text-secondary',
          'prose-p:text-secondary prose-p:my-2',
          'prose-ul:my-2 prose-ol:my-2',
          'prose-li:my-0.5',
          'prose-blockquote:border-l-primary prose-blockquote:text-grey-600',
          'prose-code:bg-grey-100 prose-code:px-1 prose-code:rounded prose-code:text-sm',
          'prose-pre:bg-grey-900 prose-pre:text-grey-100 prose-pre:rounded-lg prose-pre:p-3',
          '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-grey-100',
          'prose-a:text-accent prose-a:no-underline hover:prose-a:underline',
          editorClassName
        ),
        style: `min-height: ${minHeight}`,
      },
      // Files arriving in the editor. Images become inline embeds when the
      // surface can upload them (onEmbedImage); everything else, and images on
      // surfaces without embeds, is staged as an attachment. Never base64.
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files || []);
        if (files.length === 0) return false;
        // A GIF copied from a page: embed the animation by URL, exactly as the
        // GIF picker does, instead of uploading the still frame in `files`.
        const gifUrl = files.every((f) => f.type.startsWith('image/'))
          ? pastedGifUrl(event.clipboardData?.getData('text/html') || '')
          : null;
        if (gifUrl && editorRef.current) {
          insertEmbedNode(editorRef.current, { src: gifUrl, alt: 'GIF' });
          return true;
        }
        return routeFiles(files);
      },
      handleDrop: (_view, event) => {
        if (!event.dataTransfer) return false;
        if (!onEmbedImageRef.current && (!enableAttachments || !onFilesAdded)) return false;

        event.preventDefault();
        event.stopPropagation(); // Prevent wrapper's onDrop from also handling this

        setIsDragging(false); // Clear drag overlay since stopPropagation blocks the wrapper's onDrop
        extractFilesFromDataTransfer(event.dataTransfer).then((files) => {
          if (files.length > 0) routeFiles(files);
        });
        return true;
      },
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Sync editor content when external content prop changes (e.g., clearing after submit)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      // Only update if the content is being cleared or set externally
      // Avoid infinite loops by checking if the new content is actually different
      if (content === '' || content === '<p></p>') {
        editor.commands.clearContent();
      } else if (content !== editor.getHTML()) {
        editor.commands.setContent(content);
      }
    }
  }, [content, editor]);

  const setLink = () => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!enableAttachments) return;
    e.preventDefault();
    setIsDragging(true);
  }, [enableAttachments]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (!onEmbedImageRef.current && (!enableAttachments || !onFilesAdded)) return;
    e.preventDefault();
    setIsDragging(false);
    extractFilesFromDataTransfer(e.dataTransfer).then((files) => {
      if (files.length > 0) routeFiles(files);
    });
  }, [enableAttachments, onFilesAdded, routeFiles]);

  if (!editor) {
    return null;
  }

  return (
    <div 
      className={cn(
        'rounded-lg border overflow-clip transition-colors',
        isDragging ? 'border-accent bg-accent/5' : 'border-grey-200',
        disabled && 'opacity-60',
        className
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Aa toggle — compact mode only, and only where the toolbar is hidden. */}
      {showToolbar && compactToolbar && (
        <div className="flex items-center justify-between border-b border-grey-100 bg-grey-50 px-2 py-1 sm:hidden">
          <button
            type="button"
            onClick={() => setFormatOpen((v) => !v)}
            disabled={disabled}
            className={cn(
              'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-colors',
              formatOpen ? 'bg-grey-200 text-secondary' : 'text-grey-500 hover:bg-grey-100',
            )}
            aria-expanded={formatOpen}
          >
            <Type className="h-4 w-4" />
            Format
          </button>
        </div>
      )}

      {/* Toolbar */}
      {showToolbar && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-grey-100 bg-grey-50 sticky top-0 z-10',
            // Compact: hidden on phones until Format is pressed. Never hidden
            // on desktop, whatever the toggle says.
            compactToolbar && !formatOpen && 'hidden sm:flex',
          )}
        >
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            disabled={disabled}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            disabled={disabled}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            disabled={disabled}
            title="Underline"
          >
            <UnderlineIcon className="w-4 h-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
            disabled={disabled}
            title="Strikethrough"
          >
            <Strikethrough className="w-4 h-4" />
          </ToolbarButton>
          
          <div className="w-px h-5 bg-grey-200 mx-1" />
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive('heading', { level: 2 })}
            disabled={disabled}
            title="Heading"
          >
            <Heading2 className="w-4 h-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            disabled={disabled}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            disabled={disabled}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            disabled={disabled}
            title="Quote"
          >
            <Quote className="w-4 h-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
            disabled={disabled}
            title="Code Block"
          >
            <Code className="w-4 h-4" />
          </ToolbarButton>
          
          <div className="w-px h-5 bg-grey-200 mx-1" />
          
          <ToolbarButton
            onClick={setLink}
            isActive={editor.isActive('link')}
            disabled={disabled}
            title="Link"
          >
            <LinkIcon className="w-4 h-4" />
          </ToolbarButton>
          
          {/* Attachment button */}
          {enableAttachments && (
            <>
              <ToolbarButton
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                title="Attach files"
              >
                <Paperclip className="w-4 h-4" />
              </ToolbarButton>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="*/*"
                className="hidden"
                // @ts-ignore
                webkitdirectory=""
                mozdirectory=""
                onChange={(e) => {
                  if (e.target.files) {
                    handleFiles(e.target.files);
                    e.target.value = '';
                  }
                }}
              />
            </>
          )}

          {/* Emoji picker button */}
          <div ref={emojiAnchorRef}>
            <ToolbarButton
              onClick={() => setShowEmojiPicker((v) => !v)}
              disabled={disabled}
              title="Insert emoji"
            >
              <Smile className="w-4 h-4" />
            </ToolbarButton>
            {showEmojiPicker && (
              <EmojiPicker
                anchorRef={emojiAnchorRef}
                onSelect={(emoji) => {
                  if (emoji !== '__close__') {
                    editor.chain().focus().insertContent(emoji).run();
                  }
                  setShowEmojiPicker(false);
                }}
              />
            )}
          </div>

          {/* GIF picker button */}
          {enableGifPicker && (
            <div ref={gifAnchorRef}>
              <ToolbarButton onClick={() => setShowGifPicker(v => !v)} disabled={disabled} title="Insert GIF">
                <Film className="w-4 h-4" />
              </ToolbarButton>
              {showGifPicker && (
                <GifPicker
                  anchorRef={gifAnchorRef}
                  onSelect={(gifUrl) => {
                    if (gifUrl !== '__close__') {
                      editor.chain().focus().setImage({ src: gifUrl }).run();
                    }
                    setShowGifPicker(false);
                  }}
                />
              )}
            </div>
          )}

          <div className="flex-1" />
          
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo() || disabled}
            title="Undo"
          >
            <Undo className="w-4 h-4" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo() || disabled}
            title="Redo"
          >
            <Redo className="w-4 h-4" />
          </ToolbarButton>
        </div>
      )}
      
      {/* Editor */}
      <div
        className={cn('px-3 py-2 bg-white relative', maxHeight && 'overflow-y-auto')}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <EditorContent editor={editor} />
        
        {/* Drag overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-accent/10 flex items-center justify-center pointer-events-none">
            <p className="text-accent font-medium">Drop files here</p>
          </div>
        )}
      </div>
      
      {/* Staged files (images show as thumbnails, others as chips) */}
      {enableAttachments && stagedFiles.length > 0 && (
        <div className="px-3 py-2 border-t border-grey-100 bg-grey-50">
          <div className="flex flex-wrap gap-2">
            {stagedFiles.map((staged) => {
              const isImage = staged.file.type.startsWith('image/');
              
              return (
                <div
                  key={staged.id}
                  className={cn(
                    'relative group flex items-center gap-2 rounded-lg border text-sm',
                    staged.error 
                      ? 'bg-error/5 border-error/20' 
                      : 'bg-white border-grey-200',
                    isImage ? 'p-1' : 'px-2 py-1.5'
                  )}
                >
                  {isImage && staged.preview ? (
                    <img 
                      src={staged.preview} 
                      alt={staged.file.name}
                      className="h-12 w-12 object-cover rounded"
                    />
                  ) : staged.uploading ? (
                    <Loader2 className="h-4 w-4 text-accent animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 text-grey-500" />
                  )}
                  
                  {!isImage && (
                    <>
                      <span className={cn(
                        'max-w-[120px] truncate',
                        staged.error && 'text-error'
                      )}>
                        {staged.file.name}
                      </span>
                      <span className="text-grey-400 text-xs">
                        {formatBytes(staged.file.size)}
                      </span>
                    </>
                  )}
                  
                  {isImage && onEmbedImage && !staged.uploading && !staged.error && (
                    <button
                      type="button"
                      onClick={() => {
                        if (staged.attachmentId) reinsertEmbed(staged);
                        else embedImages([staged.file]);
                        onFileRemoved?.(staged.id);
                      }}
                      className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full border border-grey-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-secondary shadow-sm hover:bg-grey-50"
                    >
                      Embed
                    </button>
                  )}

                  {onFileRemoved && !staged.uploading && (
                    <button
                      type="button"
                      onClick={() => onFileRemoved(staged.id)}
                      className={cn(
                        'text-grey-400 hover:text-error transition-colors',
                        isImage 
                          ? 'absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border border-grey-200' 
                          : 'p-0.5'
                      )}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Drag hint for toolbar mode. Hidden on phones: there is no dragging
          there, so the row is a strip of screen spent saying nothing. */}
      {showToolbar && enableAttachments && (
        <div className="hidden px-3 py-1.5 border-t border-grey-100 bg-grey-50 sm:block">
          <span className="text-xs text-grey-400">or paste / drag files here</span>
        </div>
      )}

      {/* Minimal toolbar for inline mode with attachments */}
      {!showToolbar && enableAttachments && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-t border-grey-100 bg-grey-50">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            tabIndex={-1}
            className="flex items-center gap-1 text-xs text-grey-500 hover:text-grey-700 transition-colors"
          >
            <Paperclip className="w-3.5 h-3.5" />
            <span>Attach</span>
          </button>
          <span className="text-xs text-grey-400">
            or paste / drag files
          </span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="*/*"
            className="hidden"
            // @ts-ignore
            webkitdirectory=""
            mozdirectory=""
            onChange={(e) => {
              if (e.target.files) {
                handleFiles(e.target.files);
                e.target.value = '';
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

// Minimal inline editor (for comments/replies) with optional attachments and mentions
export function InlineRichTextEditor({
  content = '',
  onChange,
  placeholder = 'Write a reply...',
  className,
  enableAttachments = false,
  stagedFiles = [],
  onFilesAdded,
  onFileRemoved,
  disabled = false,
  enableMentions = false,
  mentionableUsers = [],
  mentionUsersLoading,
  onMention,
  enableEveryoneMention = false,
}: {
  content?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
  enableAttachments?: boolean;
  stagedFiles?: StagedFile[];
  onFilesAdded?: (files: File[]) => void;
  onFileRemoved?: (fileId: string) => void;
  disabled?: boolean;
  enableMentions?: boolean;
  mentionableUsers?: MentionUser[];
  mentionUsersLoading?: boolean;
  onMention?: (userId: string) => void;
  enableEveryoneMention?: boolean;
}) {
  return (
    <RichTextEditor
      content={content}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      minHeight="60px"
      showToolbar={false}
      enableAttachments={enableAttachments}
      stagedFiles={stagedFiles}
      onFilesAdded={onFilesAdded}
      onFileRemoved={onFileRemoved}
      disabled={disabled}
      enableMentions={enableMentions}
      mentionableUsers={mentionableUsers}
      mentionUsersLoading={mentionUsersLoading}
      onMention={onMention}
      enableEveryoneMention={enableEveryoneMention}
    />
  );
}

// Re-export MentionUser type + everyone-mention constants
export type { MentionUser };
export { EVERYONE_MENTION, EVERYONE_MENTION_ID };
